/**
 * AI Readiness Lead Magnet Modal
 *
 * Shared public modal used by marketing surfaces to capture a verified
 * company email before background report delivery.
 *
 * Primary responsibilities:
 * - Wrap marketing CTAs in a consistent readiness modal
 * - Pair the email-only form with a concise preparation explainer
 *
 * Dependencies:
 * - generated marketing asset
 * - lead magnet form
 * - ui/expandable-screen
 */

'use client'

import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { GeneratedMarketingAsset } from '@/components/marketing/generated-marketing-asset'
import { AiReadinessLeadMagnetForm } from '@/components/quick-ai-readiness/lead-magnet-form'
import {
  ExpandableScreen,
  ExpandableScreenContent,
  ExpandableScreenTrigger
} from '@/components/ui/expandable-screen'
import { getMarketingCopy } from '@/lib/marketing-copy'
import { AiReadinessLeadMagnetSource } from '@/lib/quick-ai-readiness/lead-magnet'

interface AiReadinessLeadMagnetModalProps {
  children: ReactNode
  source: AiReadinessLeadMagnetSource
}

export function AiReadinessLeadMagnetModal({
  children,
  source
}: AiReadinessLeadMagnetModalProps) {
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)

  return (
    <ExpandableScreen
      animationDuration={0.34}
      contentRadius="0px"
      layoutId={`ai-readiness-${source}`}
      triggerRadius="999px"
    >
      <ExpandableScreenTrigger className="w-full sm:w-auto [&>*]:w-full sm:[&>*]:w-auto">
        {children}
      </ExpandableScreenTrigger>
      <ExpandableScreenContent
        className="fixed inset-0 min-h-dvh max-w-none overflow-y-auto border-0 shadow-none"
        closeButtonClassName="right-3 top-3 sm:right-5 sm:top-5"
      >
        <h2 className="sr-only">{copy.leadMagnet.form.modalTitle}</h2>
        <p className="sr-only">
          {copy.leadMagnet.form.modalDescription}
        </p>

        <div className="mx-auto grid min-h-dvh w-full max-w-[1320px] gap-4 px-4 py-16 sm:px-6 lg:grid-cols-[minmax(320px,0.92fr)_minmax(420px,1.08fr)] lg:items-stretch lg:gap-5 lg:py-6 xl:px-8">
          <div className="relative hidden min-h-full overflow-hidden rounded-[1.85rem] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(240,244,249,0.94))] lg:flex lg:flex-col lg:justify-between dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,22,32,0.98),rgba(9,14,22,0.96))]">
            <div className="relative min-h-[42dvh] overflow-hidden border-b border-slate-200/80 dark:border-white/10">
              <GeneratedMarketingAsset
                assetKey="aiReadinessAnalysis"
                className="absolute inset-0"
                frame={false}
                imageClassName="scale-[1.03] object-cover"
                priority
                sizes="(min-width: 1280px) 520px, 40vw"
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0)_42%,rgba(244,247,251,0.92)_100%)] dark:bg-[linear-gradient(180deg,rgba(8,12,18,0)_38%,rgba(9,14,22,0.96)_100%)]" />
              <div className="absolute left-6 top-6">
                <p className="inline-flex rounded-full border border-slate-300/80 bg-white/88 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-600 backdrop-blur dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                  {copy.leadMagnet.label}
                </p>
              </div>
            </div>

            <div className="relative grid gap-5 p-6 xl:p-7">
              <div>
                <h3 className="font-display max-w-[13ch] text-3xl font-medium leading-[0.96] tracking-[-0.02em] text-slate-950 xl:text-4xl dark:text-slate-50">
                  {copy.leadMagnet.title}
                </h3>
                <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {copy.leadMagnet.description}
                </p>
              </div>

              <div className="grid gap-3">
                {copy.leadMagnet.afterSubmitSteps.map((step, index) => (
                  <div
                    key={step}
                    className="rounded-[1.4rem] border border-slate-200/80 bg-white/74 px-4 py-3 dark:border-white/10 dark:bg-white/[0.04]"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      {`0${index + 1}`}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-200">
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <AiReadinessLeadMagnetForm
            source={source}
            variant="modal"
            className="w-full max-w-screen-sm place-self-center lg:max-w-none"
          />
        </div>
      </ExpandableScreenContent>
    </ExpandableScreen>
  )
}
